/**
 * The workspace rail beside the page tree (spec §8.25, M24), assembled from the page rail's parts:
 * `buildRailEdges` over page-tree rows, `layoutRail` with DOM row geometry, `RailOverlay`.
 */
import type { TreeID } from "loro-crdt";
import { useMemo, type RefObject } from "react";
import type { PageRow } from "../pages/pageTree";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { layoutRail, type EdgePath } from "./layout";
import { buildRailEdges } from "./model";
import { RailOverlay, type OverlayEndpoints } from "./RailOverlay";
import { useBundleOptions, useQueryTracks, useRailConfig, useRailStyles } from "./useRail";
import { readAllWorkspaceLinks } from "../links/model";
import { aggregateRelations, iriResolver, relationsOf, useAggregatedLinks, useDomRowGeometry, type WorkspaceLink } from "./workspace";

const NO_LINKS: WorkspaceLink[] = [];

export function useWorkspaceRail({ handle, workspaceId, allRows, visibleRows, listRef }: { handle: DocHandle; workspaceId: string; allRows: PageRow[]; visibleRows: PageRow[]; listRef: RefObject<HTMLElement | null> }) {
  const config = useRailConfig(handle.doc);
  const styles = useRailStyles(handle.doc);
  const bundleOptions = useBundleOptions();
  // the selector reads the document only: `useLoroSelector` caches per document version, and a
  // selector that also read `allRows` kept the empty rows of the first render once the document
  // loaded, so a fresh visit drew no relation until the next change (maintainer's report, 2026-10-17)
  const links = useLoroSelector(handle.doc, readAllWorkspaceLinks, (a, b) => JSON.stringify(a) === JSON.stringify(b)) ?? NO_LINKS;
  const pageLinks = useMemo(() => relationsOf(links, allRows), [links, allRows]);
  const aggregate = useAggregatedLinks(workspaceId, config.enabled && !!config.blockLinks);
  const relations = useMemo(
    () => (config.blockLinks && aggregate.data ? [...pageLinks, ...aggregateRelations(aggregate.data, allRows)] : pageLinks),
    [pageLinks, aggregate.data, config.blockLinks, allRows],
  );
  const { states: queryTracks } = useQueryTracks(workspaceId, handle.crdtDocId, config, true);
  const resolve = useMemo(() => iriResolver(allRows), [allRows]);
  const geometry = useDomRowGeometry(listRef, visibleRows.map((r) => r.treeId).join(","));

  const rail = useMemo(() => {
    if (!config.enabled) return null;
    const queryResults = new Map([...queryTracks].map(([id, st]) => [id, st.edges]));
    const { edges, groups } = buildRailEdges({ docId: handle.crdtDocId, config, styles, relations, groups: new Map(), queryResults, allRows, visibleRows, resolveIri: resolve });
    if (!edges.length) return null;
    const endpoints = new Map<string, OverlayEndpoints>(edges.map((e) => [e.id, { from: visibleRows[e.from]!.treeId as TreeID, to: visibleRows[e.to]!.treeId as TreeID, trackId: e.id.split(":")[0]! }]));
    return { edges, groups, endpoints };
  }, [config, styles, relations, queryTracks, allRows, visibleRows, resolve, handle.crdtDocId]);

  const layout = rail && geometry.rows.length === visibleRows.length ? layoutRail(geometry.rows, rail.edges, rail.groups, bundleOptions) : null;
  const jump = (path: EdgePath) => {
    const edge = rail?.edges.find((e) => e.id === path.id);
    if (!edge) return;
    // the far end from where the reader looks: the page list is short, so the target end will do
    document.querySelector(`[data-page-row="${visibleRows[edge.to]!.treeId}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  };
  const overlay = layout && rail ? <RailOverlay layout={layout} height={geometry.height} endpoints={rail.endpoints} onJump={jump} /> : null;
  const gutters = layout ? { left: layout.leftGutter, right: layout.rightGutter } : undefined;
  // the menu's scope: what it lists and how it counts query rows
  const scope = useMemo(() => ({ relations, drawn: (s: string, o: string) => !!resolve(s) && !!resolve(o) }), [relations, resolve]);
  return { overlay, gutters, scope };
}
